import { useState } from "react";
import { TbX, TbPlus, TbTrash, TbSearch } from "react-icons/tb";
import { toast } from "react-toastify";
import { Auth } from "aws-amplify";
import appConfig from "../../../../config/app-config";
import MaskEdit from "../../../organisms/MaskEdit";
import SelectTextField from "../../../molecules/SelectTextField";
import TextEdit from "../../../molecules/TextEdit";
import CurrencyInput from "../../../molecules/CurrencyInput";

const { address: PERSON_API_URL, key: PERSON_API_KEY } = appConfig.apis.personApiEnv;

export interface Nominee {
    socialId: string;
    name: string;
    email: string;
    mobile: string;
    fromDatabase: boolean;
}

export interface VerificationRequestData {
    operation: string;
    operationType: string;
    value: string;
    nominees: Nominee[];
}

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onSend: (data: VerificationRequestData) => void;
}

const emptyNominee = (): Nominee => ({ socialId: "", name: "", email: "", mobile: "", fromDatabase: false });

const ALL_OPERATIONS = ['CORE', 'KEEP', 'CLEAN', 'FORFAITING', 'SHIELD', 'FLOW', 'Aditamento'];

const SignatoryVerificationModal: React.FC<Props> = ({ isOpen, onClose, onSend }) => {
    const [operation, setOperation] = useState("");
    const [operationType, setOperationType] = useState("");
    const [value, setValue] = useState<number | string>(0);
    const [nominees, setNominees] = useState<Nominee[]>([emptyNominee()]);
    const [searching, setSearching] = useState<number | null>(null);
    const [sending, setSending] = useState(false);

    const getHeaders = async () => {
        const session = await Auth.currentSession();
        const token = session.getIdToken().getJwtToken();
        return { 'x-api-key': PERSON_API_KEY, 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
    };

    const formatPhone = (raw: string) => {
        const digits = (raw || "").replace(/\D/g, "");
        if (digits.length === 11) return `(${digits.slice(0,2)}) ${digits.slice(2,7)}-${digits.slice(7)}`;
        if (digits.length === 10) return `(${digits.slice(0,2)}) ${digits.slice(2,6)}-${digits.slice(6)}`;
        return raw;
    };

    const searchByCpf = async (index: number, rawValue?: string) => {
        const cpf = (rawValue || nominees[index].socialId).replace(/\D/g, "");
        if (cpf.length !== 11) { toast.warn("CPF inválido"); return; }
        setSearching(index);
        try {
            const headers = await getHeaders();
            const res = await fetch(`${PERSON_API_URL}/individual/page-list?name=${cpf}&email=&mobile=&page=0&size=10&sort=id,DESC`, { headers });
            if (res.ok) {
                const data = await res.json();
                const list = data.list || [];
                if (list.length > 0) {
                    const person = list[0];
                    setNominees(prev => {
                        if (!prev[index] || prev[index].socialId.replace(/\D/g, "") !== cpf) return prev;
                        const updated = [...prev];
                        updated[index] = {
                            socialId: prev[index].socialId,
                            name: person.name || person.fullName || "",
                            email: person.email || "",
                            mobile: formatPhone(person.mobile || person.mobilePhone || ""),
                            fromDatabase: true,
                        };
                        return updated;
                    });
                    toast.success("Pessoa encontrada na base");
                    return;
                }
            }
            toast.info("Pessoa não encontrada. Preencha manualmente.");
        } catch { toast.error("Erro ao buscar CPF"); }
        finally { setSearching(null); }
    };

    const updateNominee = (index: number, field: keyof Nominee, val: string) => {
        const updated = [...nominees];
        updated[index] = { ...updated[index], [field]: val, fromDatabase: field === "socialId" ? false : updated[index].fromDatabase };
        setNominees(updated);
        if (field === "socialId" && val.replace(/\D/g, "").length === 11) {
            searchByCpf(index, val);
        }
    };

    const addNominee = () => setNominees([...nominees, emptyNominee()]);
    const removeNominee = (index: number) => { if (nominees.length > 1) setNominees(nominees.filter((_, i) => i !== index)); };

    const handleSend = async () => {
        if (!operation.trim()) { toast.warn("Informe a operação"); return; }
        if (!operationType) { toast.warn("Informe o tipo de operação"); return; }
        if (!value || value === 0) { toast.warn("Informe o valor"); return; }
        const validNominees = nominees.filter(n => n.socialId.replace(/\D/g, "").length > 0 || n.name.trim());
        setSending(true);
        try {
            await onSend({ operation, operationType, value: String(value), nominees: validNominees });
            setOperation(""); setOperationType(""); setValue(0); setNominees([emptyNominee()]);
            onClose();
        } catch { toast.error("Erro ao enviar solicitação"); }
        finally { setSending(false); }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="flex justify-between items-center p-4 border-b">
                    <h2 className="text-lg font-semibold text-gray-800">Solicitar Verificação de Signatários</h2>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><TbX size={20} /></button>
                </div>

                <div className="p-4 space-y-4">
                    <div className="grid grid-cols-3 gap-4">
                        <div>
                            <TextEdit id="operation" label="Operação *" value={operation} onChange={(e: any) => setOperation(e.target.value)} />
                        </div>
                        <div>
                            <SelectTextField label="Tipo de Operação *" value={operationType} onChange={(e: any) => setOperationType(e.target.value)} items={ALL_OPERATIONS.map(op => ({ value: op, label: op }))} emptyLabel="Selecione..." />
                        </div>
                        <div>
                            <CurrencyInput id="value" label="Valor (R$) *" prefix="R$" fractionDigits={2} value={value} onChange={(v: number) => setValue(v)} />
                        </div>
                    </div>

                    <div>
                        <div className="flex justify-between items-center mb-2">
                            <label className="text-sm font-medium text-gray-700">Pessoas Indicadas</label>
                            <button onClick={addNominee} className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800">
                                <TbPlus size={14} /> Adicionar pessoa
                            </button>
                        </div>

                        {nominees.map((nominee, idx) => (
                            <div key={idx} className="border border-gray-200 rounded p-3 mb-2">
                                <div className="flex items-center gap-2 mb-2">
                                    <div className="flex-1">
                                        <label className="block text-xs text-gray-500 mb-1">CPF</label>
                                        <div className="flex gap-1">
                                            <MaskEdit mask="___.___.___-__" replacement={{ '_': /\d/ }} label="" value={nominee.socialId} onChange={(e: any) => updateNominee(idx, "socialId", e.target.value)} className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm" placeholder="000.000.000-00" />
                                            <button onClick={() => searchByCpf(idx)} disabled={searching === idx} className="px-2 py-1 text-blue-600 hover:bg-blue-50 rounded" title="Buscar na base">
                                                <TbSearch size={16} />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="flex-1">
                                        <label className="block text-xs text-gray-500 mb-1">Nome</label>
                                        <input type="text" value={nominee.name} onChange={e => updateNominee(idx, "name", e.target.value)} className="w-full border border-gray-300 rounded px-2 py-1 text-sm disabled:bg-gray-100" placeholder="Nome completo" readOnly={nominee.fromDatabase} disabled={nominee.socialId.replace(/\D/g, "").length < 11 && !nominee.name} />
                                    </div>
                                    {nominees.length > 1 && (
                                        <button onClick={() => removeNominee(idx)} className="mt-4 p-1 text-red-500 hover:bg-red-50 rounded"><TbTrash size={16} /></button>
                                    )}
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    <div>
                                        <label className="block text-xs text-gray-500 mb-1">Email</label>
                                        <input type="email" value={nominee.email} onChange={e => updateNominee(idx, "email", e.target.value)} className="w-full border border-gray-300 rounded px-2 py-1 text-sm disabled:bg-gray-100" placeholder="email@exemplo.com" readOnly={nominee.fromDatabase} disabled={nominee.socialId.replace(/\D/g, "").length < 11 && !nominee.email} />
                                    </div>
                                    <div>
                                        <label className="block text-xs text-gray-500 mb-1">Celular</label>
                                        <MaskEdit mask="(__) _____-____" replacement={{ '_': /\d/ }} label="" value={nominee.mobile} onChange={(e: any) => updateNominee(idx, "mobile", e.target.value)} className="w-full border border-gray-300 rounded px-2 py-1 text-sm disabled:bg-gray-100" placeholder="(00) 00000-0000" readOnly={nominee.fromDatabase} disabled={nominee.socialId.replace(/\D/g, "").length < 11 && !nominee.mobile} />
                                    </div>
                                </div>
                                {nominee.fromDatabase && <span className="text-xs text-green-600 mt-1 inline-block">✓ Dados carregados da base</span>}
                            </div>
                        ))}
                    </div>
                </div>

                <div className="flex justify-end gap-2 p-4 border-t">
                    <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-50">Cancelar</button>
                    <button onClick={handleSend} disabled={sending} className="px-4 py-2 text-sm text-white bg-blue-500 rounded hover:bg-blue-600 disabled:opacity-50">
                        {sending ? "Enviando..." : "Enviar Solicitação"}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SignatoryVerificationModal;
